'use client';

import React, { useEffect, useRef, useState } from 'react';
import { TrendingUp, Users, Award, Clock } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';


const stats = [
  { id: 'stat-projects', value: 340, suffix: '+', label: 'Projects Delivered', sublabel: 'Across 28 countries', icon: Award, color: 'text-primary', bgColor: 'bg-primary/10' },
  { id: 'stat-clients', value: 200, suffix: '+', label: 'Happy Clients', sublabel: 'NPS score 94/100', icon: Users, color: 'text-accent', bgColor: 'bg-accent/10' },
  { id: 'stat-years', value: 8, suffix: ' yrs', label: 'Years of Excellence', sublabel: 'Since 2018', icon: Clock, color: 'text-secondary', bgColor: 'bg-secondary/10' },
  { id: 'stat-satisfaction', value: 98, suffix: '%', label: 'Client Satisfaction', sublabel: 'Based on 180+ reviews', icon: TrendingUp, color: 'text-emerald', bgColor: 'bg-emerald/10' },
];

function useCountUp(target: number, duration = 2000, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime: number;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [target, duration, start]);
  return count;
}

function StatCard({ stat, animate }: { stat: typeof stats[0]; animate: boolean }) {
  const count = useCountUp(stat.value, 2000, animate);
  const Icon = stat.icon;
  return (
    <div className="glass-card rounded-2xl p-6 card-hover border border-border/40 group">
      <div className="flex items-start justify-between mb-4">
        <div className={`w-12 h-12 rounded-xl ${stat.bgColor} flex items-center justify-center`}>
          <Icon size={22} className={stat.color} />
        </div>
        <div className="text-right">
          <span className="text-3xl font-display font-extrabold stat-value text-foreground">
            {animate ? count : 0}{stat.suffix}
          </span>
        </div>
      </div>
      <p className="font-display font-semibold text-foreground text-base mb-1">{stat.label}</p>
      <p className="text-xs text-muted-foreground">{stat.sublabel}</p>
    </div>
  );
}

export default function StatsSection() {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={ref} className="py-20 relative overflow-hidden">
      <div className="max-w-screen-2xl mx-auto px-6 lg:px-8 xl:px-10 2xl:px-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          {stats.map((stat) => (
            <StatCard key={stat.id} stat={stat} animate={visible} />
          ))}
        </div>
      </div>
    </section>
  );
}